import React from 'react';
import { useState } from 'react';
import { Music, Star, DollarSign, Headphones, Piano, Volume2, X, Shield, CheckCircle, Clock, Phone, Mail } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PianoNumeriquesOccasion = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();
  const [selectedPiano, setSelectedPiano] = useState<any>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const pianos = [
    {
      brand: "Yamaha CLP-645",
      image: "/image11.jpg",
      condition: "Excellent état",
      features: ["88 touches lestées", "Échantillonnage CFX", "Garantie 2 ans", "Révisé par nos soins"],
      specs: {
        clavier: "88 touches lestées GrandTouch",
        sons: "38 voix dont CFX et Bösendorfer",
        polyphonie: "256 notes",
        amplification: "2 x 30W",
        dimensions: "135.7 x 42.2 x 84.9 cm",
        poids: "57 kg",
        etat: "Excellent - Révisé 2024",
        garantie: "2 ans"
      }
    },
    {
      brand: "Roland HP-704",
      image: "/imageskawai.jpeg",
      condition: "Très bon état",
      features: ["SuperNATURAL Piano", "Bluetooth intégré", "Garantie 1 an", "Entretien complet"],
      specs: {
        clavier: "88 touches PHA-50",
        sons: "324 sons SuperNATURAL",
        polyphonie: "256 notes",
        amplification: "2 x 30W",
        dimensions: "137.5 x 43.1 x 108.1 cm",
        poids: "54.5 kg",
        etat: "Très bon - Entretien 2024",
        garantie: "1 an"
      }
    },
    {
      brand: "Kawai CN39",
      image: "/image12.jpg",
      condition: "Bon état",
      features: ["Responsive Hammer III", "Harmonic Imaging", "Garantie 1 an", "Contrôlé qualité"],
      specs: {
        clavier: "88 touches Responsive Hammer III",
        sons: "355 sons Harmonic Imaging",
        polyphonie: "192 notes",
        amplification: "2 x 20W",
        dimensions: "136 x 40.5 x 86 cm",
        poids: "43 kg",
        etat: "Bon - Contrôlé 2024",
        garantie: "1 an"
      }
    }
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Pianos Numériques <span className="heading-accent">d'Occasion</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              Qualité exceptionnelle à prix attractifs. Nos pianos numériques d'occasion 
              sont soigneusement sélectionnés et révisés par nos experts.
            </p>
          </div>

          {/* Avantages */}
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal grid md:grid-cols-4 gap-6 mb-20"
          >
            {[
              { icon: DollarSign, title: "Le Prix", desc: "Moins coûteux" },
              { icon: Volume2, title: "Volume Réglable", desc: "Volume sonore réglable" },
              { icon: Headphones, title: "Jeu au Casque", desc: "Possibilité de jouer avec un casque" },
              { icon: Piano, title: "Qualité Authentique", desc: "Un très bon piano numérique se rapproche d'un piano acoustique" }
            ].map((feature, index) => (
              <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                <feature.icon className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 font-light text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collection */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef3 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h2 className="text-3xl font-light text-center text-white mb-16">
              Opportunités <span className="heading-accent">Exceptionnelles</span>
            </h2>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {pianos.map((piano, index) => (
                <div key={index} className="glass-card interactive-card rounded-2xl overflow-hidden light-effect">
                  <div className="relative h-64">
                    <img
                      src={piano.image}
                      alt={piano.brand}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div className="absolute top-4 right-4">
                      <span className="bg-green-400/20 backdrop-blur-sm text-green-400 px-3 py-1 rounded-full text-sm font-semibold border border-green-400/30">
                        {piano.condition}
                      </span>
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-red-400/20 backdrop-blur-sm text-red-400 px-3 py-1 rounded-full text-sm font-semibold border border-red-400/30">
                        Occasion
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-semibold text-white">{piano.brand}</h3>
                      <span className="text-sm font-light text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/30">
                        Prix sur devis
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-6">
                      {piano.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-white/70">
                          <Star className="w-3 h-3 text-yellow-400 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Processus de sélection */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto">
            <h3 className="text-3xl font-light text-center text-white mb-8">
              Notre <span className="heading-accent">Processus Qualité</span>
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {[
                { step: "1", title: "Sélection", desc: "Choix rigoureux des instruments" },
                { step: "2", title: "Révision", desc: "Contrôle technique complet" },
                { step: "3", title: "Garantie", desc: "Protection et service après-vente" }
              ].map((process, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-yellow-400/30">
                    <span className="text-yellow-400 font-semibold">{process.step}</span>
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">{process.title}</h4>
                  <p className="text-white/60 font-light text-sm">{process.desc}</p>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <button className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide">
                Voir Toutes les Occasions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal des détails */}
      {selectedPiano && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">{selectedPiano.brand}</h3>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <img 
                src={selectedPiano.image} 
                alt={selectedPiano.brand}
                className="w-full h-48 object-cover rounded-2xl mb-6"
              />
              
              <div className="mb-4">
                <span className="bg-green-400/20 backdrop-blur-sm text-green-400 px-3 py-1 rounded-full text-sm font-semibold border border-green-400/30">
                  {selectedPiano.condition}
                </span>
              </div>
              
              <h4 className="text-lg font-semibold text-white mb-4">Caractéristiques Techniques</h4>
              
              <div className="space-y-4">
                {Object.entries(selectedPiano.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-start py-2 border-b border-white/10">
                    <span className="text-white/70 font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-white text-right max-w-xs">{value}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex gap-4">
                <button 
                  onClick={() => {
                    setSelectedPiano(null);
                    scrollToContact();
                  }}
                  className="btn-premium px-6 py-3 rounded-lg font-medium flex-1"
                >
                  Contactez-nous
                </button>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-6 py-3 rounded-lg font-light transition-all duration-300 flex-1"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PianoNumeriquesOccasion;